// src/components/StudentTutorPanel.jsx
// Linked tutors (2026-09-28), student side: shows which tutor's dashboard is
// active, lets a student with more than one tutor switch between them (the
// choice is saved, so it's also what they land on next login), and lets any
// student add another tutor by typing that tutor's name and cohort code
// (deliberately no list of tutors to pick from). A tutor with approval
// switched on holds the new link as pending until they confirm the student.
// All the real work is server-side (joinTutorCohort / switchActiveTutor) -
// after either, the page reloads so the whole dashboard picks up the change.

import React, { useState, useEffect } from 'react';
import { Users, UserPlus, X } from 'lucide-react';
import { getMyTutors, joinTutor, switchTutor } from '../firebase/tutorLinks';

const inputStyle = {
  width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: '8px',
  fontSize: '14px', background: 'white', boxSizing: 'border-box'
};

const StudentTutorPanel = ({ user, compact = false }) => {
  const [tutors, setTutors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [tutorName, setTutorName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getMyTutors().then(setTutors).catch(err => console.error('Get my tutors error:', err));
  }, []);

  const handleSwitch = async (orgId) => {
    if (orgId === user.orgId) return;
    setBusy(true);
    try {
      await switchTutor(orgId);
      window.location.reload();
    } catch (err) {
      alert(err.message || 'Could not switch tutor. Please try again.');
      setBusy(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!tutorName.trim() || !code.trim()) { setError("Please enter your tutor's name and the cohort code they gave you."); return; }
    setBusy(true);
    setError('');
    try {
      const result = await joinTutor({ tutorName: tutorName.trim(), cohortCode: code.trim() });
      if (!result.alreadyLinked) {
        // Same "new registration" notice the tutor gets for a brand-new student
        try {
          const { sendDirectNotification, NOTIFICATION_TYPES } = await import('../firebase/headMarkerNotifications');
          await sendDirectNotification(NOTIFICATION_TYPES.NEW_REGISTRATION, {
            studentName: user.name, studentEmail: user.email, studentPhone: user.phone || '', cohortCode: result.cohortCode
          }, result.orgId);
        } catch (notifyErr) { console.error('New tutor notification error:', notifyErr); }
      }
      window.location.reload();
    } catch (err) {
      setError(err.message || "Could not add that tutor. Please check the name and code with your tutor.");
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: compact ? 0 : '14px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: compact ? 'center' : 'flex-start' }}>
      {tutors.length > 1 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#4a5568', fontWeight: 600 }}>
          <Users size={16} />
          Tutor:
          <select
            value={user.orgId}
            disabled={busy}
            onChange={e => handleSwitch(e.target.value)}
            style={{ padding: '6px 10px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white', cursor: busy ? 'wait' : 'pointer' }}
          >
            {tutors.map(t => (
              <option key={t.orgId} value={t.orgId}>{t.name}{t.pending ? ' (awaiting approval)' : ''}</option>
            ))}
          </select>
        </label>
      )}
      <button
        type="button"
        onClick={() => { setShowAdd(v => !v); setError(''); }}
        style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}
      >
        <UserPlus size={14} />
        Add another tutor
      </button>

      {showAdd && (
        <form onSubmit={handleAdd} style={{ flexBasis: '100%', marginTop: '6px', padding: '16px', background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '8px', maxWidth: '460px', textAlign: 'left', marginLeft: compact ? 'auto' : 0, marginRight: compact ? 'auto' : 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong style={{ fontSize: '14px', color: '#2d3748' }}>Add another tutor</strong>
            <button type="button" onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096', padding: 0 }} aria-label="Close">
              <X size={16} />
            </button>
          </div>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#4a5568' }}>
            Enter your new tutor's name and the cohort code they gave you. You'll keep your existing tutor too, and can switch between them here. Your new tutor may need to confirm you before you can see their homework.
          </p>
          <div style={{ marginBottom: '10px' }}>
            <input value={tutorName} onChange={e => setTutorName(e.target.value)} placeholder="Tutor's name" style={inputStyle} disabled={busy} autoComplete="off" />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <input value={code} onChange={e => setCode(e.target.value)} placeholder="Cohort code" style={{ ...inputStyle, textTransform: 'uppercase' }} disabled={busy} autoComplete="off" />
          </div>
          {error && <div style={{ marginBottom: '10px', color: '#991b1b', fontSize: '13px' }}>{error}</div>}
          <button type="submit" disabled={busy} style={{ padding: '10px 18px', background: '#c33329', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '14px', cursor: busy ? 'wait' : 'pointer' }}>
            {busy ? 'Adding...' : 'Add tutor'}
          </button>
        </form>
      )}
    </div>
  );
};

export default StudentTutorPanel;
